import { useEffect, useState } from 'react'
import { getWindow } from '@ztechapps/common.utils'

/**
 * Hook to adjust modal height when virtual keyboard appears on mobile devices.
 * Uses the Visual Viewport API to detect keyboard presence and calculate available height.
 *
 * @returns Available height as a CSS value string (e.g., '85vh', '500px', 'calc(100vh - 300px)')
 *
 * @example
 * ```tsx
 * const height = useKeyboardAwareHeight('85vh')
 * return <div style={{ height }}>...</div>
 * ```
 */
export const useKeyboardAwareHeight = (defaultHeight: string): string => {
  const [adjustedHeight, setAdjustedHeight] = useState(defaultHeight)

  useEffect(() => {
    const win = getWindow()
    if (!win || !win.visualViewport) {
      return
    }

    const visualViewport = win.visualViewport

    const handleResize = () => {
      // Calculate the difference between layout viewport and visual viewport
      // When keyboard appears, visual viewport height decreases
      const viewportHeight = visualViewport.height
      const windowHeight = win.innerHeight

      // Keyboard is likely visible if visual viewport is significantly smaller
      const isKeyboardVisible = windowHeight - viewportHeight > 150

      if (isKeyboardVisible) {
        // Adjust height to fit within visible area
        // Leave some padding (20px) to prevent content being right at edge
        const availableHeight = viewportHeight - 20
        setAdjustedHeight(`${availableHeight}px`)
      } else {
        // Reset to default height when keyboard is hidden
        setAdjustedHeight(defaultHeight)
      }
    }

    // Initial check
    handleResize()

    // Listen for viewport changes (keyboard show/hide, orientation change)
    visualViewport.addEventListener('resize', handleResize)
    visualViewport.addEventListener('scroll', handleResize)

    return () => {
      visualViewport.removeEventListener('resize', handleResize)
      visualViewport.removeEventListener('scroll', handleResize)
    }
  }, [defaultHeight])

  return adjustedHeight
}
